import {
  AuthStatusChangeComments,
  isValidAuthStatusChangeComments,
} from "../../../data-model/authStatusChangeComments";
import { LocalStorageKeys } from "../../../enums/localStorageKeys";
import {
  getItemFromLocalStorage,
  removeItemFromLocalStorage,
  setItemInLocalStorage,
} from "../../../utils/localStorageHelper";

/**
 * Retrieves saved authorization status change comments from local storage for a specific order.
 *
 * @param orderId - The unique identifier of the order
 * @returns An array of authorization status change comments if found, otherwise null
 */
export const getSavedAuthStatusChangeCommentsFromLocalStorage = (
  orderId: string,
): AuthStatusChangeComments[] | null => {
  const key = `${LocalStorageKeys.AUTH_STATUS_CHANGE_COMMENTS}_${orderId}`;
  const comments = getItemFromLocalStorage(key);
  return comments ?? null;
};

/**
 * Removes a specific authorization status change comment from local storage based on J-code.
 * Finds and removes the comment matching the provided J-code from the stored comments array.
 *
 * @param orderId - The unique identifier of the order
 * @param j_code - The J-code identifying which comment to remove
 */
export const removeAuthStatusChangesFromLocalStorageByJCode = (
  orderId: string,
  j_code: string,
) => {
  const key = `${LocalStorageKeys.AUTH_STATUS_CHANGE_COMMENTS}_${orderId}`;
  const comments: AuthStatusChangeComments[] | null =
    getItemFromLocalStorage(key);

  if (comments && comments !== null) {
    const index = comments.findIndex((comment) => comment.j_code === j_code);
    if (index !== -1) {
      comments.splice(index, 1);
      setItemInLocalStorage(key, comments);
    }
  }
};

/**
 * Adds a new authorization status change comment to local storage for a specific order.
 * If comments already exist and are valid, appends the new comment; otherwise creates a new array.
 *
 * @param orderId - The unique identifier of the order
 * @param comment - The authorization status change comment to add
 */
export const pushAuthStatusChangeCommentsToLocalStorage = (
  orderId: string,
  comment: AuthStatusChangeComments,
) => {
  const key = `${LocalStorageKeys.AUTH_STATUS_CHANGE_COMMENTS}_${orderId}`;
  const comments: AuthStatusChangeComments[] | null =
    getItemFromLocalStorage(key);
  if (
    comments &&
    comments !== null &&
    isValidAuthStatusChangeComments(comments ?? [])
  ) {
    comments.push(comment);
    setItemInLocalStorage(key, comments ?? []);
  } else {
    setItemInLocalStorage(key, [comment]);
  }
};

/**
 * Removes all authorization status change comments from local storage for a specific order.
 *
 * @param orderId - The unique identifier of the order
 */
export const clearAuthStatusChangeCommentsFromLocalStorage = (
  orderId: string,
) => {
  const key = `${LocalStorageKeys.AUTH_STATUS_CHANGE_COMMENTS}_${orderId}`;
  removeItemFromLocalStorage(key);
};
